
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Message } from '@/types/chat';

export const useChatRealtime = (
  userId: string | undefined,
  currentCommunityId: string | undefined,
  currentConversationId: string | null,
  onMessageReceived: (message: Message) => void,
  onUnreadCountUpdate: (count: number) => void,
  markMessagesAsRead: (conversationId: string) => Promise<void>
) => {
  useEffect(() => {
    if (!userId || !currentCommunityId) return;
    
    console.log('[useChatRealtime] Setting up realtime subscription for user:', userId);
    
    const messageSub = supabase
      .channel(`messages:${userId}`)
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'messages',
          filter: `receiver_id=eq.${userId}`
        },
        async (payload) => {
          console.log('[useChatRealtime] Received message update:', payload);
          
          if (payload.new) {
            const newMessage = payload.new as Message;
            
            // Only process messages that belong to a valid conversation
            if (newMessage.conversation_id) {
              onMessageReceived(newMessage);
              
              // If the message is new and we're viewing it, mark as read
              if (!newMessage.read_at && newMessage.receiver_id === userId && 
                  currentConversationId && newMessage.conversation_id === currentConversationId) {
                await markMessagesAsRead(newMessage.conversation_id);
              }
            }
            
            // Update total unread count
            const { count } = await supabase
              .from('messages')
              .select('*', { count: 'exact', head: true })
              .eq('receiver_id', userId)
              .is('read_at', null);
              
            onUnreadCountUpdate(count || 0);
          }
        }
      )
      .subscribe();
    
    return () => {
      console.log('[useChatRealtime] Cleaning up realtime subscription');
      supabase.removeChannel(messageSub);
    };
  }, [userId, currentCommunityId, currentConversationId, onMessageReceived, onUnreadCountUpdate, markMessagesAsRead]);
};

export const useChatTyping = (
  userId: string | undefined,
  currentCommunityId: string | undefined,
  onTypingUpdate: (updater: (prev: Record<string, { userId: string; conversationId: string; isTyping: boolean }>) => Record<string, { userId: string; conversationId: string; isTyping: boolean }>) => void
) => {
  const typingChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!userId || !currentCommunityId) return;
    
    console.log('[useChatTyping] Setting up typing indicators channel for user:', userId);
    
    const typingChannel = supabase
      .channel('typing_indicators')
      .on('broadcast', { event: 'typing' }, (payload) => {
        console.log('[useChatTyping] Received typing indicator:', payload);
        const { userId: typingUserId, conversationId, isTyping } = payload;
        
        onTypingUpdate((prev: Record<string, { userId: string; conversationId: string; isTyping: boolean }>) => {
          console.log('[useChatTyping] Updating typing status:', {
            userId: typingUserId,
            conversationId,
            isTyping,
            previousState: prev
          });
          return {
            ...prev,
            [typingUserId]: { userId: typingUserId, conversationId, isTyping }
          };
        });
      })
      .subscribe((status) => {
        console.log('[useChatTyping] Typing channel subscription status:', status);
      });
    
    typingChannelRef.current = typingChannel;
    
    return () => {
      console.log('[useChatTyping] Cleaning up typing indicators channel');
      typingChannel.unsubscribe();
      typingChannelRef.current = null;
    };
  }, [userId, currentCommunityId, onTypingUpdate]);

  const broadcastTyping = (conversationId: string, isTyping: boolean) => {
    if (!userId || !typingChannelRef.current) {
      console.log('[useChatTyping] Cannot set typing status - missing user or channel:', {
        userId: userId,
        hasChannel: !!typingChannelRef.current
      });
      return;
    }
    
    console.log('[useChatTyping] Broadcasting typing status:', {
      userId,
      conversationId,
      isTyping
    });
    
    typingChannelRef.current.send({
      type: 'broadcast',
      event: 'typing',
      payload: {
        userId,
        conversationId,
        isTyping
      }
    });
  };

  return { broadcastTyping };
};
